import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, X, Shield, ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';

// Simple Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white border border-gray-100 rounded-2xl w-full max-w-lg p-6 shadow-2xl animate-scaleIn">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>
                <div className="text-gray-600 text-sm max-h-[60vh] overflow-y-auto custom-scrollbar space-y-4">
                    {children}
                </div>
            </div>
        </div>
    );
};

const AdminLogin = () => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [activeModal, setActiveModal] = useState(null);

    // Forgot Password State
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotMessage, setForgotMessage] = useState(null);

    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Always pass 'admin' as role
            const result = await login(formData.email, formData.password, 'admin');
            if (!result.success) {
                setError(result.message || 'Login failed. Please try again.');
                return;
            }
            navigate('/dashboard');
        } catch (err) {
            setError('Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        setForgotMessage(null);

        try {
            const response = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail })
            });
            const data = await response.json();

            if (data.success) {
                setForgotMessage({ type: 'success', text: data.message });
                setForgotEmail('');
            } else {
                setForgotMessage({ type: 'error', text: data.message || 'Failed to send reset link.' });
            }
        } catch (err) {
            setForgotMessage({ type: 'error', text: 'An error occurred. Please try again.' });
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex overflow-hidden font-sans bg-gray-50 relative">
            {/* Back to Home Button */}
            <button
                onClick={() => navigate('/')}
                className="absolute top-6 left-6 z-20 flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors font-medium bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-100 hover:border-gray-300 shadow-sm"
            >
                <ArrowLeft size={16} /> <span className="hidden sm:inline">Back to Home</span>
            </button>

            {/* Mobile Background Image */}
            <div className="lg:hidden absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 to-pink-100/20"></div>
                <img
                    src={process.env.PUBLIC_URL + "/login_visual_admin.png"}
                    alt="Background"
                    className="w-full h-full object-cover object-center"
                />
            </div>

            {/* Left Side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6 lg:p-12 relative z-10 lg:bg-white">
                <div className="w-full max-w-sm space-y-5 bg-white/25 backdrop-blur-md lg:bg-transparent p-5 sm:p-6 lg:p-0 rounded-2xl lg:rounded-none shadow-2xl lg:shadow-none border border-white/50 lg:border-0">

                    {/* Header */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Shield className="text-purple-600" size={24} />
                            </div>
                            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight">
                                Admin Portal
                            </h1>
                        </div>
                        <p className="text-gray-500 text-xs sm:text-sm">Secure administrator access only.</p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg text-red-700 text-sm flex items-center animate-shake">
                            <span className="font-medium mr-1">Error:</span> {error}
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-1.5">
                            <label className="block text-sm font-bold text-gray-700 ml-1">Email or ID</label>
                            <input
                                type="text"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-5 py-3.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:bg-white transition-all font-medium"
                                placeholder="Enter admin email or ID"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between ml-1">
                                <label className="block text-sm font-bold text-gray-700">Password</label>
                                <button
                                    type="button"
                                    onClick={() => { setActiveModal('forgotPassword'); setForgotMessage(null); setForgotEmail(formData.email || ''); }}
                                    className="text-xs font-semibold text-purple-600 hover:text-purple-800 hover:underline"
                                >
                                    Forgot Password?
                                </button>
                            </div>
                            <div className="relative group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-5 py-3.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:bg-white transition-all font-medium pr-12"
                                    placeholder="Enter password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-purple-600 hover:bg-purple-700 shadow-purple-500/30 text-white font-bold py-3.5 rounded-xl hover:shadow-lg active:scale-[0.99] transition-all flex items-center justify-center space-x-2 text-base"
                        >
                            {loading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Processing...</span>
                                </>
                            ) : (
                                <span>Login as Administrator</span>
                            )}
                        </button>
                    </form>

                    {/* Footer Links */}
                    <div className="text-center pt-4 border-t border-gray-100 mt-6">
                        <p className="text-xs text-gray-400">
                            Protected Admin Area • Unauthorized access prohibited
                        </p>
                    </div>

                </div>
            </div>

            {/* Right Side - Visual (Desktop Only) */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-gray-50 items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-100/30 to-pink-100/30"></div>
                <div className="relative z-10 w-full h-full">
                    <img
                        src={process.env.PUBLIC_URL + "/login_visual_admin.png"}
                        alt="Admin Portal"
                        className="w-full h-full object-cover object-center"
                    />
                </div>
            </div>

            {/* Forgot Password Modal */}
            <Modal
                isOpen={activeModal === 'forgotPassword'}
                onClose={() => setActiveModal(null)}
                title="Reset Password"
            >
                <div className="space-y-4">
                    <p className="text-gray-500 text-sm">Enter your admin email address and we'll send you a link to reset your password.</p>

                    {forgotMessage && (
                        <div className={`p-4 border-l-4 rounded-r-lg text-sm flex items-start gap-2 ${forgotMessage.type === 'success' ? 'bg-green-50 border-green-500 text-green-700' : 'bg-red-50 border-red-500 text-red-700'}`}>
                            {forgotMessage.type === 'success' ? <CheckCircle size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />}
                            <span>{forgotMessage.text}</span>
                        </div>
                    )}

                    <form onSubmit={handleForgotPassword} className="space-y-4 pt-2">
                        <div className="relative">
                            <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="email"
                                required
                                className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-5 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:bg-white pl-12"
                                placeholder="Enter admin email"
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={forgotLoading}
                            className="w-full bg-purple-600 text-white font-bold py-3 rounded-xl hover:bg-purple-700 transition"
                        >
                            {forgotLoading ? 'Sending Link...' : 'Send Reset Link'}
                        </button>
                    </form>
                </div>
            </Modal>

            <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-shake {
          animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
        }
      `}</style>
        </div>
    );
};

export default AdminLogin;
